'use client';

import { useParams, useRouter } from 'next/navigation';
import { FC, useRef, useState, useEffect } from 'react';

import { Refresh } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import ButtonBase from '@mui/material/ButtonBase';
import ArrowBack from '@mui/icons-material/ArrowBack';
import {
  Box,
  Card,
  Grid,
  Paper,
  Stack,
  Button,
  Divider,
  Container,
  Typography,
} from '@mui/material';

import Image from '../../../../components/image';
import { useLocales } from '../../../../locales';
import Iconify from '../../../../components/iconify';
import { useResponsive } from '../../../../hooks/use-responsive';
import { ActionTypes, useAppContext } from '../../../../contexts/AppContext';
import FirstWaveShapeIcon from '../../../../assets/icons/FirstWaveShapeIcon';
import SecondWaveShapeIcon from '../../../../assets/icons/SecondWaveShapeIcon';
import { BlockType, ComponentTypeProps } from '../../../../types/page-generator';

const CryptoCard: FC<ComponentTypeProps> = ({ block }) => {
  const isMobile = useResponsive('down', 'sm');
  const localParams = useParams();
  const router = useRouter();
  const [viewCvv, setViewCvv] = useState(false);
  const { currentLang } = useLocales();

  const defaultText =
    'Using this card, any online transaction OTP will be sent as an app notification. Please continue with the transaction using any text or email OTP received during the payment process.';

  const alertText: any = block?.alertText;

  const buttonLabel: any = block?.buttonLabel;
  const buttonHref: any = block?.buttonHref;
  const buttonIcon: any = block?.buttonIcon;
  const buttonSize: any = block?.buttonSize;
  const buttonBgColor: any = block?.buttonBgColor;
  const buttonColor: any = block?.buttonColor;
  const buttonModal: any = block?.buttonModal;

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const entry of entries) {
        if (entry.contentRect) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <Container ref={containerRef} maxWidth="sm" sx={{ p: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: currentLang.value === 'he' ? 'row-reverse' : 'row',
        }}
      >
        <Stack direction={currentLang.value === 'he' ? 'row-reverse' : 'row'} alignItems="center">
          <IconButton color="inherit" onClick={() => router.back()}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6">Card</Typography>
        </Stack>
        <IconButton color="inherit">
          <Refresh />
        </IconButton>
      </Box>

      {/* Пунктирный фон только за карточкой */}

      <Box
        sx={{
          width: 1,
          mb: 2,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          mt: 2,
        }}
      >
        <Card
          sx={{
            width: '100%',
            backgroundImage: 'url(/assets/images/card-font.png)',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100%',
            // background: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)',
            borderRadius: 3,
            p: 3,
            color: 'white',
            boxShadow: 6,
            backgroundColor: '#fff', // Белый фон
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 150,
            position: 'relative',
          }}
        >
          <Typography variant="h6" textAlign="start">
            **** **** **** ****
          </Typography>
          <Typography variant="subtitle1" textAlign="start" sx={{ mt: 2 }}>
            Harel Gobani
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 6,
              width: '100%',
            }}
          >
            <Stack direction="row" width={1} justifyContent="space-between" alignItems="center">
              <Button
                sx={{
                  borderRadius: '15px',
                  bgcolor: 'green',
                  '&: hover': { bgcolor: 'green' },
                }}
              >
                Approved
              </Button>

              <Stack
                alignItems="center"
                onClick={() => setViewCvv(!viewCvv)}
                sx={{ cursor: 'pointer' }}
              >
                <Typography variant="body2">CVV </Typography>
                {viewCvv ? (
                  <Typography variant="body2">852</Typography>
                ) : (
                  <Typography variant="body2">***</Typography>
                )}
              </Stack>

              <Stack alignItems="center">
                <Typography variant="body2">EXPIRY </Typography>
                <Typography variant="body2">XX/XX </Typography>
              </Stack>
            </Stack>
          </Box>
        </Card>
      </Box>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          background: block?.settings?.infoBackgroundColor || '#000000',
          borderRadius: 3,
          p: 2,
          mb: 1,
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Grid
            container
            spacing={1}
            sx={{
              justifyContent: 'center',
              flexDirection: currentLang.value === 'he' ? 'row-reverse' : 'row',
            }}
          >
            <Grid
              item
              xs={4}
              sx={{
                display: 'flex',
                width: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src="/assets/images/hand-wallet.png"
                sx={{
                  width: isMobile ? '50px' : '50px',
                }}
              />
            </Grid>

            <Grid item xs={4}>
              <Stack>
                <Typography
                  variant={isMobile ? 'h6' : 'h1'}
                  align="center"
                  sx={{ color: '#FFC107 ' }}
                >
                  91.14
                </Typography>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ color: 'white', fontSize: isMobile ? '12px' : '14px' }}
                >
                  Card Balance (USD)
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={4}>
              <Typography />
            </Grid>
          </Grid>
          <Stack alignItems="center">
            <Divider sx={{ width: '90%', my: 1 }} />
          </Stack>
          <Typography variant="subtitle2" align="center" sx={{ mt: 1, color: 'white' }}>
            Card Name: CryptoCard Virtual
          </Typography>
        </Box>
      </Box>
      {block?.settings?.enableButton && (
        <Stack alignItems="center">
          {currentLang.value === 'he' ? (
            <Button
              variant="contained"
              startIcon={<Iconify icon={buttonIcon || ''} sx={{ color: buttonColor || 'white' }} />}
              onClick={() => router.push(`/${localParams.lng}/${buttonHref}`)}
              sx={{
                mb: 3,
                bgcolor: buttonBgColor || '#3f3456',
                borderRadius: '15px',
                '&:hover': { bgcolor: buttonBgColor || '#3f3456' },
              }}
            >
              {buttonLabel || ''}
            </Button>
          ) : (
            <Button
              variant="contained"
              endIcon={<Iconify icon={buttonIcon || ''} sx={{ color: buttonColor || 'white' }} />}
              onClick={() => router.push(`/${localParams.lng}/${buttonHref}`)}
              sx={{
                mb: 3,
                bgcolor: buttonBgColor || '#3f3456',
                borderRadius: '15px',
                '&:hover': { bgcolor: buttonBgColor || '#3f3456' },
              }}
            >
              {buttonLabel || ''}
            </Button>
          )}
        </Stack>
      )}
      {block?.settings?.enableAlert && (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              background: block?.settings?.alertBackground || 'pink',
              borderRadius: '10px',
              p: 2,
            }}
          >
            <Iconify
              icon={block?.settings?.alertIcon || 'fluent:warning-24-regular'}
              sx={{
                color: block?.settings?.alertColor || 'red',
                width: '100px',
              }}
            />
            <Typography
              sx={{
                fontSize: block?.settings?.alertFontsize || '12px',
                textAlign: 'justify',
                color: block?.settings?.alertColor || 'red',
              }}
            >
              {alertText || defaultText}
            </Typography>
          </Stack>
        </Box>
      )}
      {block?.settings?.enableCardService && <CardServicePanel block={block} />}
    </Container>
  );
};

type Props = {
  block: BlockType;
};

function CardServicePanel({ block }: Props) {
  const combinedBlock: any = block?.combinedBlock || [];
  const localParams = useParams();
  const router = useRouter();
  const { currentLang } = useLocales();
  const { dispatch, state } = useAppContext();

  const setActiveDialog = (payload: null | string) => {
    dispatch({ type: ActionTypes.SET_ACTIVE_DIALOG, payload });
  };

  const firstStopNavigate = block?.settings?.firstGradientColorStopNavigate
    ? `${block?.settings?.firstGradientColorStopNavigate}%`
    : '40%';
  const secondStopNavigate = block?.settings?.secondGradientColorStopNavigate
    ? `${block?.settings?.secondGradientColorStopNavigate}%`
    : '100%';

  const colorGradientNavigate = block?.settings?.addGradientStyleNavigate
    ? `linear-gradient(90deg, ${block?.settings?.firstGradientColorNavigate} ${firstStopNavigate}, ${block?.settings?.secondGradientColorNavigate} ${secondStopNavigate} )`
    : '';

  const view = (item: any) =>
    block?.settings?.variant === '1' ? (
      <ButtonBase
        sx={{
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'inline-block',
        }}
      >
        <Paper
          elevation={6} // Чем больше — тем выше тень
          sx={{
            backgroundColor: '#fff', // Или другой цвет фона, не 'transparent'
            borderRadius: '50%',
            // border: '1px solid black',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 80,
            width: 80,
            boxShadow: (theme) => theme.shadows[6], // можно явно указать тень
          }}
        >
          <Iconify
            icon={item.buttonIcon}
            sx={{
              width: 30,
              height: 30,
              color: item.buttonColor || '#FFC107',
            }}
          />
          <Typography
            variant="body1"
            sx={{
              mt: 1,
              color: item.buttonColor || '#FFC107',
              fontSize: item?.buttonFontSize || '12px',
              textAlign: 'center',
            }}
          >
            {item.buttonTitle}
          </Typography>
        </Paper>
      </ButtonBase>
    ) : (
      <Paper
        elevation={6}
        sx={{
          // background: '#2a1e38',
          // backgroundImage: `url('/assets/images/button-font.png')`,
          background: block?.settings?.addGradientStyleNavigate ? colorGradientNavigate : '',
          borderRadius: 3,
          padding: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 120,
          width: 180,
        }}
      >
        <Iconify
          icon={item.buttonIcon}
          sx={{
            width: '30px',
            height: '30px',
            color: item.buttonColor || '#FFC107',
          }}
        />
        <Typography
          variant="body1"
          sx={{
            mt: 1,
            color: item.buttonColor || '#FFC107',
            fontSize: item?.buttonFontSize || '12px',
            textAlign: 'center',
          }}
        >
          {item.buttonTitle}
        </Typography>
      </Paper>
    );

  return (
    <Box sx={{ textAlign: 'left', color: 'white' }}>
      <Typography
        variant="h6"
        sx={{
          my: 2,
          color: 'black',
          textAlign: currentLang.value === 'he' ? 'end' : 'start',
        }}
      >
        Card Service
      </Typography>
      <Stack alignItems="center">
        <Box
          sx={{
            width: block?.settings?.serviceButtonVariant === '2' ? '70%' : '100%',
          }}
        >
          {/* Верхний ряд: 2 карточки */}
          <Grid container spacing={2} justifyContent="center">
            {combinedBlock.map((item: any, index: number) => (
              <Grid
                item
                xs={block?.settings?.serviceButtonVariant === '2' ? 6 : 4}
                key={index}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'center',
                }}
                onClick={() => {
                  if (item?.buttonHref && item?.buttonHref !== '#' && !item?.buttonDrawer) {
                    router.push(`/${localParams.lng}/${item?.buttonHref}`);
                  }
                  if (item?.buttonHref === '#' && item.buttonDrawer && item.buttonModal) {
                    setActiveDialog(item.buttonModal);
                  }
                }}
              >
                {view(item)}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
      {/* Нижняя карточка по центру */}
    </Box>
  );
}

export default CryptoCard;
